/**
 * Editor — main writing surface.
 * Desktop: sidebar on left, toolbar across the top, editor in the middle.
 * Mobile: tiny header (home / resources / undo / redo). Sidebar opens as a
 * drawer from the top. Editor takes the full width.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { AppHeader } from "@/components/Header";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ScreenplayEditor, newBlankScript } from "@/components/editor/ScreenplayEditor";
import { ResourcesSidebar } from "@/components/editor/ResourcesSidebar";
import { ToolsSidebar } from "@/components/editor/ToolsSidebar";
import { StoryboardView } from "@/components/storyboard/StoryboardView";
import { ShareModal } from "@/components/editor/ShareModal";
import { NotepadPanel } from "@/components/editor/NotepadPanel";
import { NotepadFullView } from "@/components/editor/NotepadFullView";
import { ScriptReadPane } from "@/components/editor/ScriptReadPane";
import { TableReadPanel } from "@/components/editor/TableReadPanel";
import { CoveragePanel } from "@/components/editor/CoveragePanel";
import { RecoveredEditsPanel } from "@/components/editor/RecoveredEditsPanel";
import { TimelinePanel } from "@/components/editor/TimelinePanel";
import { LaneReadView } from "@/components/editor/LaneReadView";
import { SupervisorPanel } from "@/components/editor/SupervisorPanel";
import { CoachPanel } from "@/components/editor/CoachPanel";
import { BoardTabPanel } from "@/components/editor/BoardTabPanel";
import { SceneCardsBoard } from "@/components/editor/SceneCardsBoard";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { analyzeScriptCharacters } from "@/lib/screenplayDetector";
import { useIsMobile } from "@/lib/useMediaQuery";
import { Save, FileDown, Settings2, Undo2, Redo2, Home, BookText, X, Share2, Search, Mic, ScrollText, History, Rows3, ClipboardCheck, Wrench, Shapes, Plus, FileText, FolderOpen, Image as ImageIcon, Palette, Trash2, ChevronDown, Pencil, Clapperboard, Compass, LayoutGrid, NotebookPen, MapPin } from "lucide-react";
import { exportToBlob, serializeAsJSON, loadFromBlob, CaptureUpdateAction } from "@excalidraw/excalidraw";
import { toast } from "sonner";

const AUTOSAVE_DEBOUNCE_MS = 1200;
const PROMPTS_STORAGE_KEY = "screenplay_show_prompts";
const PAGE_BREAKS_STORAGE_KEY = "screenplay_show_page_breaks";
const MOBILE_FORMAT_STORAGE_KEY = "screenplay_mobile_format";
const SCENE_NUMBERS_STORAGE_KEY = "screenplay_show_scene_numbers";
const ANNOTATIONS_STORAGE_KEY = "screenplay_show_annotations";
const TOOLS_COLLAPSED_KEY = "screenplay_tools_collapsed";
const AUTOPOP_DEBOUNCE_MS = 3500;

function readBool(key, defaultValue) {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === "1";
  } catch {
    return defaultValue;
  }
}
function writeBool(key, v) {
  try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* ignore */ }
}

export default function Editor() {
  const { scriptId } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const isMobile = useIsMobile();
  const confirm = useConfirm();

  const editorRef = useRef(null);
  const [script, setScript] = useState(null);
  const [elements, setElements] = useState([]);
  const [title, setTitle] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [charactersRefreshKey, setCharactersRefreshKey] = useState(0);
  const [pageInfo, setPageInfo] = useState({ current: 1, total: 1 });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showPrompts, setShowPrompts] = useState(() => readBool(PROMPTS_STORAGE_KEY, true));
  const [showPageBreaks, setShowPageBreaks] = useState(() => readBool(PAGE_BREAKS_STORAGE_KEY, true));
  const [mobileFormat, setMobileFormat] = useState(() => readBool(MOBILE_FORMAT_STORAGE_KEY, false));
  const [showSceneNumbers, setShowSceneNumbers] = useState(() => readBool(SCENE_NUMBERS_STORAGE_KEY, false));
  const [annotationsOn, setAnnotationsOn] = useState(() => readBool(ANNOTATIONS_STORAGE_KEY, false));
  const [annotations, setAnnotations] = useState([]);
  const [annReportId, setAnnReportId] = useState(null);
  const [accessRole, setAccessRole] = useState("owner");
  const [shareOpen, setShareOpen] = useState(false);
  const [tableReadOpen, setTableReadOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notepadFull, setNotepadFull] = useState(null);
  const notepadNoteRef = useRef(null);
  const [recoveredOpen, setRecoveredOpen] = useState(false);
  const [conflictCount, setConflictCount] = useState(0);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineFull, setTimelineFull] = useState(false);
  const [laneRead, setLaneRead] = useState(null);
  const [supervisorOpen, setSupervisorOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachPrefill, setCoachPrefill] = useState(null);
  const [rightCollapsed, setRightCollapsed] = useState(() => {
    const sp = user?.right_panel_prefs;
    return typeof sp?.collapsed === "boolean" ? sp.collapsed : readBool(TOOLS_COLLAPSED_KEY, true);
  });
  const [rightTab, setRightTab] = useState(() => user?.right_panel_prefs?.activeTab || "feedback");
  const [linkedBoards, setLinkedBoards] = useState([]);
  const [activeTab, setActiveTab] = useState("script");
  const [storyboardOpened, setStoryboardOpened] = useState(false);
  const [cardsOpened, setCardsOpened] = useState(false);
  const [openedBoardTabs, setOpenedBoardTabs] = useState(() => new Set());
  const [boardStatus, setBoardStatus] = useState({});
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [editingTab, setEditingTab] = useState(null); // "script" | boardId
  const [editValue, setEditValue] = useState("");
  const [bgOpen, setBgOpen] = useState(false);
  const [boardsMenuOpen, setBoardsMenuOpen] = useState(false);
  const editOriginalRef = useRef("");
  const boardApisRef = useRef({});
  const boardFileInputRef = useRef(null);
  const pendingTitleSaveRef = useRef(false);

  const canEdit = ["owner", "admin", "cowriter"].includes(accessRole);
  const isOwner = ["owner", "admin"].includes(accessRole);
  const canComment = ["owner", "admin", "cowriter", "feedback"].includes(accessRole);
  const canModerate = ["owner", "admin", "cowriter"].includes(accessRole);
  const anyToolOpen = tableReadOpen || timelineOpen;
  const canCreateBoard = canEdit && user?.account_type !== "collaborator";

  // Right Tools sidebar — which tools this user can see, open/collapse control.
  const toolTabs = useMemo(() => {
    const t = [];
    if (canComment) t.push("feedback");
    if (canEdit) t.push("supervisor", "coverage", "coach");
    return t;
  }, [canComment, canEdit]);

  const toggleRightCollapsed = useCallback(() => {
    setRightCollapsed((c) => { writeBool(TOOLS_COLLAPSED_KEY, !c); return !c; });
  }, []);

  const dirty = useRef(false);
  const debounceRef = useRef(null);
  const autoPopRef = useRef(null);
  const charCleanupRef = useRef(null);
  const presentCharsRef = useRef(null);
  const dismissedCharsRef = useRef(new Set());
  const versionRef = useRef(1);
  const baseElements = useRef([]);

  const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

  const toggleAnnotations = useCallback((v) => {
    setAnnotationsOn(v);
    writeBool(ANNOTATIONS_STORAGE_KEY, v);
  }, []);

  const applyAnnotations = useCallback((list, reportId) => {
    setAnnotations(list);
    if (reportId) setAnnReportId(reportId);
  }, []);

  // Coverage → Coach handoff: open the Coach with the dimension's path pre-filled.
  const handleCoachFocus = useCallback((text) => {
    setCoachPrefill({ text, nonce: Date.now() });
    if (isMobile) {
      setCoverageOpen(false);
      setCoachOpen(true);
    } else {
      setRightCollapsed(false);
      setRightTab("coach");
    }
  }, [isMobile]);

  const rereadAnnotation = useCallback(async (annotationId, scope = "scene") => {
    const { data } = await api.post(`/scripts/${scriptId}/coverage/${annReportId}/annotations/${annotationId}/reread`, { scope });
    setAnnotations((prev) => prev.map((a) => (a.id === data.annotation.id ? data.annotation : a)));
    return data.annotation;
  }, [scriptId, annReportId]);

  // On load, restore margin notes from the newest annotated read (if the toggle was on).
  useEffect(() => {
    if (!annotationsOn) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/scripts/${scriptId}/coverage`);
        const withNotes = (data.reports || []).find((r) => r.annotations_status === "ready" && (r.annotations || []).length);
        if (!cancelled && withNotes) setAnnotations(withNotes.annotations);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [scriptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshConflicts = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/conflicts`);
      setConflictCount((data.conflicts || []).length);
    } catch { /* ignore */ }
  }, [scriptId]);

  const togglePrompts = useCallback(() => setShowPrompts((p) => { writeBool(PROMPTS_STORAGE_KEY, !p); return !p; }), []);
  const togglePageBreaks = useCallback(() => setShowPageBreaks((p) => { writeBool(PAGE_BREAKS_STORAGE_KEY, !p); return !p; }), []);
  const toggleMobileFormat = useCallback(() => setMobileFormat((p) => { writeBool(MOBILE_FORMAT_STORAGE_KEY, !p); return !p; }), []);
  const toggleSceneNumbers = useCallback(() => setShowSceneNumbers((p) => { writeBool(SCENE_NUMBERS_STORAGE_KEY, !p); return !p; }), []);

  // -------------------- Linked boards (Script ⇄ Board tabs) --------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/boards", { params: { script_id: scriptId } });
        if (alive) setLinkedBoards(data.boards || []);
      } catch { /* no board tabs on error (e.g. collaborators) */ }
    })();
    return () => { alive = false; };
  }, [scriptId]);

  const openBoardTab = useCallback((boardId) => {
    setOpenedBoardTabs((prev) => { const n = new Set(prev); n.add(boardId); return n; });
    setActiveTab(boardId);
  }, []);

  const createLinkedBoard = useCallback(async () => {
    if (creatingBoard) return;
    setCreatingBoard(true);
    try {
      const { data } = await api.post("/boards", { title: (title || "Untitled").trim() || "Untitled", script_ids: [scriptId] });
      setLinkedBoards((prev) => [data.board, ...prev]);
      openBoardTab(data.board.id);
    } catch (e) {
      if (e.response?.status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the board.");
    } finally {
      setCreatingBoard(false);
    }
  }, [creatingBoard, scriptId, title, openBoardTab, navigate]);

  const deleteBoard = useCallback(async (boardId) => {
    const b = linkedBoards.find((x) => x.id === boardId);
    const ok = await confirm({
      title: `Delete "${b?.title || "this board"}"?`,
      description: "The board and everything on it will be permanently removed.",
      confirmLabel: "Delete board",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/boards/${boardId}`);
      setBoardsMenuOpen(false);
      setLinkedBoards((prev) => prev.filter((x) => x.id !== boardId));
      setOpenedBoardTabs((prev) => { const n = new Set(prev); n.delete(boardId); return n; });
      delete boardApisRef.current[boardId];
      setActiveTab((cur) => (cur === boardId ? "script" : cur));
      toast.success("Board deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete the board.");
    }
  }, [linkedBoards, confirm]);

  // -------------------- Tab renaming (double-click) --------------------
  const startRename = useCallback((key, current) => {
    setEditingTab(key);
    setEditValue(current);
    editOriginalRef.current = current;
  }, []);

  const cancelRename = useCallback(() => setEditingTab(null), []);

  const commitRename = useCallback(async () => {
    const key = editingTab;
    const v = (editValue || "").trim();
    setEditingTab(null);
    if (!key || !v || v === editOriginalRef.current) return;
    if (key === "script") {
      dirty.current = true;
      pendingTitleSaveRef.current = true; // saved by the effect below, once state settles
      setTitle(v);
    } else {
      try {
        await api.put(`/boards/${key}`, { title: v });
        setLinkedBoards((prev) => prev.map((b) => (b.id === key ? { ...b, title: v } : b)));
      } catch {
        toast.error("Couldn't rename the board.");
      }
    }
  }, [editingTab, editValue]);

  // -------------------- Embedded board actions (slim strip) --------------------
  const activeBoardApi = useCallback(() => boardApisRef.current[activeTab], [activeTab]);

  // Programmatic updateScene() doesn't reliably fire Excalidraw's onChange, so
  // strip actions (background / clear / open) persist the scene explicitly.
  const persistBoardScene = useCallback((boardId, opts = {}) => {
    const exApi = boardApisRef.current[boardId];
    if (!exApi) return;
    setBoardStatus((prev) => ({ ...prev, [boardId]: { ...prev[boardId], saving: true } }));
    setTimeout(async () => {
      try {
        const payload = {
          data: {
            elements: exApi.getSceneElements(),
            appState: { viewBackgroundColor: opts.viewBackgroundColor || exApi.getAppState()?.viewBackgroundColor || "#ffffff" },
          },
        };
        if (opts.includeFiles) payload.files = exApi.getFiles() || {};
        await api.put(`/boards/${boardId}`, payload);
        setBoardStatus((prev) => ({ ...prev, [boardId]: { saving: false, savedAt: Date.now() } }));
      } catch {
        setBoardStatus((prev) => ({ ...prev, [boardId]: { ...prev[boardId], saving: false } }));
        toast.error("Couldn't save the board.");
      }
    }, 200);
  }, []);

  const boardFileName = useCallback((ext) => {
    const b = linkedBoards.find((x) => x.id === activeTab);
    return `${(b?.title || "board").replace(/[^a-z0-9-]+/gi, "_")}.${ext}`;
  }, [linkedBoards, activeTab]);

  const handleBoardFileChosen = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const exApi = activeBoardApi();
    if (!file || !exApi) return;
    try {
      const scene = await loadFromBlob(file, exApi.getAppState(), exApi.getSceneElements());
      if (scene.files) exApi.addFiles(Object.values(scene.files));
      exApi.updateScene({ elements: scene.elements, appState: scene.appState });
      persistBoardScene(activeTab, { includeFiles: true });
    } catch {
      toast.error("Couldn't open that file — expected a .excalidraw export.");
    }
  }, [activeBoardApi, activeTab, persistBoardScene]);

  const boardSaveToFile = useCallback(() => {
    const exApi = activeBoardApi();
    if (!exApi) return;
    const json = serializeAsJSON(exApi.getSceneElements(), exApi.getAppState(), exApi.getFiles(), "local");
    downloadBlob(new Blob([json], { type: "application/json" }), boardFileName("excalidraw"));
  }, [activeBoardApi, boardFileName]);

  const boardExportImage = useCallback(async () => {
    const exApi = activeBoardApi();
    if (!exApi) return;
    const els = exApi.getSceneElements();
    if (!els.length) { toast.info("Add something to the board first."); return; }
    try {
      const blob = await exportToBlob({
        elements: els, files: exApi.getFiles(),
        appState: { ...exApi.getAppState(), exportBackground: true },
        mimeType: "image/png",
      });
      downloadBlob(blob, boardFileName("png"));
    } catch { toast.error("Export failed"); }
  }, [activeBoardApi, boardFileName]);

  const setBoardBackground = useCallback((color) => {
    const exApi = activeBoardApi();
    if (!exApi) return;
    exApi.updateScene({ appState: { viewBackgroundColor: color }, captureUpdate: CaptureUpdateAction.NEVER });
    setBgOpen(false);
    persistBoardScene(activeTab, { viewBackgroundColor: color });
  }, [activeBoardApi, activeTab, persistBoardScene]);

  const boardClearCanvas = useCallback(async () => {
    const exApi = activeBoardApi();
    if (!exApi) return;
    const ok = await confirm({
      title: "Clear this board?",
      description: "Everything on the canvas will be removed. This can't be undone.",
      confirmLabel: "Clear board",
      tone: "danger",
    });
    if (ok) {
      exApi.updateScene({ elements: [] });
      persistBoardScene(activeTab);
    }
  }, [activeBoardApi, activeTab, persistBoardScene, confirm]);

  // -------------------- Load --------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/scripts/${scriptId}`);
        if (cancelled) return;
        const s = data.script;
        setScript(s);
        setAccessRole(data.access_role || "owner");
        if (data.access_role === "feedback") { setFeedbackOpen(true); setRightTab("feedback"); setRightCollapsed(false); }
        setTitle(s.title || "Untitled");
        versionRef.current = s.version || 1;
        baseElements.current = s.elements || [];
        const els = (s.elements || []).length ? s.elements : newBlankScript();
        setElements(els);
        requestAnimationFrame(() => editorRef.current?.loadElements(els));
        refreshConflicts();
      } catch (e) {
        if (e.response?.status === 404) {
          toast.error("Script not found"); navigate("/app");
        } else if (e.response?.status === 401) {
          navigate("/login");
        } else {
          toast.error(formatApiError(e.response?.data?.detail) || "Failed to load script");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [scriptId, navigate, refreshConflicts]);

  // -------------------- Save --------------------
  const save = useCallback(async (opts = {}) => {
    if (!scriptId) return;
    if (saving && !opts.force) return;
    setSaving(true);
    try {
      const current = editorRef.current?.getElements() || elements;
      const { data } = await api.post(`/scripts/${scriptId}/sync`, {
        base_version: versionRef.current, elements: current, title,
      });
      if (data.status === "applied") {
        versionRef.current = data.version;
        baseElements.current = current;
        dirty.current = false;
        setSavedAt(new Date());
        if (opts.notify) toast.success("Saved");
        return;
      }
      // conflict — merge against the live server version (element-level last-edit-wins)
      const { data: r } = await api.post(`/scripts/${scriptId}/sync/resolve`, {
        base_version: data.server_version,
        base_elements: baseElements.current,
        elements: current,
        title,
      });
      const merged = r.elements || [];
      versionRef.current = r.version;
      baseElements.current = merged;
      dirty.current = false;
      setSavedAt(new Date());
      setElements(merged);
      editorRef.current?.loadElements(merged);
      if (r.archived > 0) {
        toast.message(`Merged changes from another device — ${r.archived} line${r.archived > 1 ? "s" : ""} saved to Recovered edits.`);
        refreshConflicts();
      } else {
        toast.message("Merged changes from another device.");
      }
    } catch (e) {
      if (e.response?.status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [scriptId, title, elements, navigate, saving, refreshConflicts]);

  // Save a tab-rename of the script title once the new title state is applied.
  useEffect(() => {
    if (pendingTitleSaveRef.current) {
      pendingTitleSaveRef.current = false;
      save({ notify: false });
    }
  }, [title, save]);

  // Cherry-pick a recovered line back into the current draft.
  const insertRecoveredLine = useCallback((line) => {
    const current = editorRef.current?.getElements() || elements;
    const newEl = {
      id: genId(), type: line.type || "action",
      text: line.text || "", extension: line.extension || null, locked: false,
    };
    let next;
    const at = current.findIndex((e) => e.id === line.element_id);
    if (at >= 0) {
      next = [...current.slice(0, at + 1), newEl, ...current.slice(at + 1)];
    } else {
      // place at the end of the recorded scene, else append
      let sceneCount = 0, insertAt = current.length;
      for (let i = 0; i < current.length; i++) {
        if (current[i].type === "scene_heading") {
          sceneCount += 1;
          if (sceneCount === line.scene_index) {
            insertAt = current.length;
            for (let j = i + 1; j < current.length; j++) {
              if (current[j].type === "scene_heading") { insertAt = j; break; }
            }
            break;
          }
        }
      }
      next = [...current.slice(0, insertAt), newEl, ...current.slice(insertAt)];
    }
    setElements(next);
    editorRef.current?.loadElements(next);
    dirty.current = true;
    save({ force: true });
  }, [elements, save]);

  // Scene Timeline helpers
  const flushDraftSave = useCallback(async () => {
    if (dirty.current) await save({ force: true, notify: false });
  }, [save]);

  // Scene Cards commit — the server rewrote the script; sync the editor to it.
  const handleCardsCommitted = useCallback((els, version) => {
    setElements(els);
    editorRef.current?.loadElements(els);
    versionRef.current = version;
    baseElements.current = els;
    dirty.current = false;
    setSavedAt(new Date());
  }, []);

  const jumpToSceneCard = useCallback((sceneId) => {
    const node = document.querySelector(`[data-element-id="${sceneId}"]`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("tr-reading");
      setTimeout(() => node.classList.remove("tr-reading"), 1400);
    }
  }, []);

  // Ordered scene headings — maps "Scene N" (Supervisor citations) to element ids.
  const sceneHeadings = useMemo(() => {
    let n = 0;
    const out = [];
    for (const el of elements) {
      if (el.type === "scene_heading") { n += 1; out.push({ number: n, id: el.id, heading: el.text }); }
    }
    return out;
  }, [elements]);

  const reorderScript = useCallback((newElements) => {
    setElements(newElements);
    editorRef.current?.loadElements(newElements);
    dirty.current = true;
    save({ force: true });
  }, [save]);

  useEffect(() => {
    if (!script) return;
    if (!dirty.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(), AUTOSAVE_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [elements, title, script, save]);

  // -------------------- Character auto-populate --------------------
  useEffect(() => {
    if (!script || !scriptId || !canEdit) return;
    if (autoPopRef.current) clearTimeout(autoPopRef.current);
    autoPopRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/scripts/${scriptId}/characters/auto-populate`);
        if (data.inserted_count > 0 || data.merged_count > 0) setCharactersRefreshKey((k) => k + 1);
      } catch { /* ignore */ }
    }, AUTOPOP_DEBOUNCE_MS);
    return () => autoPopRef.current && clearTimeout(autoPopRef.current);
  }, [elements, script, scriptId, canEdit]);

  // -------------------- Character cleanup prompt --------------------
  // When a character that previously appeared in the script is fully removed
  // from it, offer to remove their profile from Resources too. Lives here (not
  // in the Characters panel) so it works regardless of which sidebar tab is
  // open and even while the writer is typing. Runs after auto-populate so a
  // brand-new cue isn't mistaken for a removal.
  useEffect(() => {
    if (!script || !scriptId || !canEdit) return undefined;
    if (charCleanupRef.current) clearTimeout(charCleanupRef.current);
    charCleanupRef.current = setTimeout(async () => {
      try {
        const els = editorRef.current?.getElements() || elements;
        const { names } = analyzeScriptCharacters(els);
        const { data } = await api.get(`/scripts/${scriptId}/characters`);
        const chars = data.characters || [];
        const keysOf = (c) => [c.name, ...((c.aliases) || [])].map((s) => (s || "").toUpperCase().trim()).filter(Boolean);
        const isPresent = (c) => keysOf(c).some((k) => names.has(k));
        const present = new Set(chars.filter(isPresent).map((c) => c.id));
        if (presentCharsRef.current === null) { presentCharsRef.current = present; return; }
        const gone = chars.filter((c) =>
          presentCharsRef.current.has(c.id) && !present.has(c.id) && !dismissedCharsRef.current.has(c.id));
        chars.forEach((c) => { if (present.has(c.id)) dismissedCharsRef.current.delete(c.id); });
        presentCharsRef.current = present;
        if (!gone.length) return;
        const namesStr = gone.map((c) => c.name).join(", ");
        const ok = await confirm({
          title: gone.length === 1 ? `Remove ${gone[0].name}?` : "Remove these characters?",
          description: gone.length === 1
            ? `${gone[0].name} no longer appears anywhere in your script. Remove this profile from Resources? Your script text won't change.`
            : `These characters no longer appear in your script: ${namesStr}. Remove their profiles from Resources? Your script text won't change.`,
          confirmLabel: "Remove",
          cancelLabel: "Keep",
          tone: "danger",
        });
        if (ok) {
          for (const c of gone) {
            // eslint-disable-next-line no-await-in-loop
            try { await api.delete(`/scripts/${scriptId}/characters/${c.id}`); } catch { /* ignore */ }
          }
          setCharactersRefreshKey((k) => k + 1);
        } else {
          gone.forEach((c) => dismissedCharsRef.current.add(c.id));
        }
      } catch { /* ignore */ }
    }, 4500);
    return () => charCleanupRef.current && clearTimeout(charCleanupRef.current);
  }, [elements, script, scriptId, canEdit, confirm]);

  const handleEditorChange = useCallback((newElements) => {
    dirty.current = true;
    setElements(newElements);
  }, []);

  const handleTitleChange = (e) => { dirty.current = true; setTitle(e.target.value); };

  const handlePageInfo = useCallback((info) => {
    setPageInfo((prev) => (prev.current === info.current && prev.total === info.total ? prev : info));
  }, []);

  // -------------------- Export --------------------
  const doExport = async (fmt) => {
    setExportOpen(false);
    await save({ force: true });
    const safe = (title || "screenplay").replace(/[^a-z0-9-]+/gi, "_");
    const ext = fmt === "pdf" ? "pdf" : fmt === "fdx" ? "fdx" : "fountain";
    const path = fmt === "pdf"
      ? `/scripts/${scriptId}/export/${fmt}?scene_numbers=${showSceneNumbers}`
      : `/scripts/${scriptId}/export/${fmt}`;
    try { await downloadFile(path, `${safe}.${ext}`); }
    catch { toast.error("Export failed"); }
  };

  const jumpToScene = (sceneIndex) => {
    const sceneElement = (elements || []).filter((e) => e.type === "scene_heading")[sceneIndex];
    if (!sceneElement) return;
    const node = document.querySelector(`[data-element-id="${sceneElement.id}"]`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.focus?.();
    }
    if (isMobile) setMobileSidebarOpen(false);
  };

  const jumpToAnchor = useCallback((elementId) => {
    const node = document.querySelector(`[data-element-id="${elementId}"]`);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    if (isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  // Persist the resources sidebar width to the user's account (follows them
  // across devices). Optimistic local update + best-effort save.
  const persistSidebarWidth = useCallback(async (w) => {
    setUser((prev) => (prev ? { ...prev, sidebar_width: w } : prev));
    try { await api.put("/auth/me", { sidebar_width: w }); } catch { /* ignore */ }
  }, [setUser]);

  const persistCardSize = useCallback(async (w, h) => {
    setUser((prev) => (prev ? { ...prev, timeline_card_w: w, timeline_card_h: h } : prev));
    try { await api.put("/auth/me", { timeline_card_w: w, timeline_card_h: h }); } catch { /* ignore */ }
  }, [setUser]);

  const persistRightWidth = useCallback(async (w) => {
    setUser((prev) => (prev ? { ...prev, right_sidebar_width: w } : prev));
    try { await api.put("/auth/me", { right_sidebar_width: w }); } catch { /* ignore */ }
  }, [setUser]);

  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const titleRef = useRef(title);
  titleRef.current = title;

  // Character locate — light up every scene where a character appears or speaks.
  const [charHighlight, setCharHighlight] = useState(null);
  const locateCharacter = useCallback(({ name, occurrences }) => {
    if (!occurrences?.length) return;
    const els = elementsRef.current || [];
    const occIds = new Set(occurrences.map((o) => o.elementId));
    const ids = [];
    let cur = [], curHas = false;
    const flush = () => { if (curHas) ids.push(...cur); };
    for (const el of els) {
      if (el.type === "scene_heading") { flush(); cur = []; curHas = false; }
      cur.push(el.id);
      if (occIds.has(el.id)) curHas = true;
    }
    flush();
    const sceneCount = new Set(occurrences.map((o) => o.sceneNumber).filter(Boolean)).size;
    setCharHighlight({ name, ids, strongIds: [...occIds], scenes: sceneCount });
    setActiveTab("script");
    setTimeout(() => jumpToAnchor(occurrences[0].elementId), 80);
  }, [jumpToAnchor]);

  const renderToolPanel = useCallback((id) => {
    if (id === "feedback") {
      if (notepadFull && !isMobile) {
        return <ScriptReadPane elements={elementsRef.current} title={titleRef.current} />;
      }
      return (
        <NotepadPanel
          embedded
          scriptId={scriptId}
          canCreate={canComment}
          canModerate={canModerate}
          currentUserId={user?.id}
          initialSelectedId={notepadNoteRef.current}
          onFullScreen={(noteId) => {
            notepadNoteRef.current = noteId;
            setNotepadFull(noteId);
            setRightCollapsed(false);
            setRightTab("feedback");
          }}
        />
      );
    }
    if (id === "supervisor") {
      return (
        <SupervisorPanel
          embedded
          open
          scriptId={scriptId}
          canEdit={canEdit}
          sceneHeadings={sceneHeadings}
          onJumpToScene={jumpToSceneCard}
        />
      );
    }
    if (id === "coverage") {
      return (
        <CoveragePanel
          embedded
          open
          scriptId={scriptId}
          annotationsOn={annotationsOn}
          onToggleAnnotations={toggleAnnotations}
          onAnnotations={applyAnnotations}
          scriptGenre={script?.genre || ""}
          onBeforeRun={flushDraftSave}
          editSignal={savedAt}
          onCoachFocus={handleCoachFocus}
        />
      );
    }
    if (id === "coach") return <CoachPanel embedded open scriptId={scriptId} onBeforeSend={flushDraftSave} prefill={coachPrefill} />;
    return null;
  }, [scriptId, canComment, canModerate, user?.id, canEdit, sceneHeadings, jumpToSceneCard, annotationsOn, toggleAnnotations, applyAnnotations, script?.genre, flushDraftSave, notepadFull, isMobile, savedAt, handleCoachFocus, coachPrefill]);

  if (!script) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)]">
        <p className="text-[var(--ink-muted)]" data-testid="editor-loading">Opening your script…</p>
      </div>
    );
  }

  // ---------------------- Render ----------------------
  return (
    <div className="h-screen flex flex-col bg-[var(--canvas)] overflow-hidden">
      <ImpersonationBanner />
      {!isMobile && <TrialBanner />}
      {!canEdit && (
        <div
          className="text-xs px-4 py-2 flex items-center justify-center gap-2 bg-[var(--brand-soft)] text-[var(--ink-secondary)]"
          data-testid="readonly-banner"
        >
          <span className="font-ui tracking-wide">
            {accessRole === "feedback"
              ? "You can read this screenplay and leave feedback for the writer."
              : "You're viewing this screenplay in read-only mode."}
          </span>
        </div>
      )}

      {/* HEADER — minimal on mobile, full on desktop */}
      {isMobile ? (
        <MobileTopBar
          title={title}
          pageInfo={pageInfo}
          onTitleChange={handleTitleChange}
          onTitleBlur={() => save({ notify: false })}
          onOpenSidebar={() => setMobileSidebarOpen(true)}
          onUndo={() => editorRef.current?.undo()}
          onRedo={() => editorRef.current?.redo()}
          onOpenSettings={() => setSettingsOpen((o) => !o)}
          canEdit={canEdit}
          showFeedback={canComment}
          onOpenFeedback={() => setFeedbackOpen(true)}
        />
      ) : (
        <AppHeader scriptTitle={title} />
      )}

      {/* MOBILE settings drop-down (positioned absolutely) */}
      {isMobile && settingsOpen && (
        <div className="absolute right-2 top-12 z-40 bg-white border border-[var(--line)] rounded-md shadow-lg py-2 min-w-[280px]" data-testid="mobile-settings-menu">
          <SettingsItems
            showPrompts={showPrompts}
            togglePrompts={togglePrompts}
            showPageBreaks={showPageBreaks}
            togglePageBreaks={togglePageBreaks}
            mobileFormat={mobileFormat}
            toggleMobileFormat={toggleMobileFormat}
            showSceneNumbers={showSceneNumbers}
            toggleSceneNumbers={toggleSceneNumbers}
          />
          <div className="border-t border-[var(--line)] mt-2 pt-2">
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setTableReadOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-table-read-btn">
                <Mic size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Table Read
              </button>
            )}
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setCoverageOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-coverage-btn">
                <ScrollText size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Coverage
              </button>
            )}
            <button onClick={() => { setSettingsOpen(false); setTimelineOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-timeline-btn">
              <Rows3 size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Timeline
            </button>
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setStoryboardOpened(true); setActiveTab("storyboard"); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-storyboard-btn">
                <Clapperboard size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Storyboard
              </button>
            )}
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setCardsOpened(true); setActiveTab("cards"); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-cards-btn">
                <LayoutGrid size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Scene Cards
              </button>
            )}
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setSupervisorOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-supervisor-btn">
                <ClipboardCheck size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Script Supervisor
              </button>
            )}
            {canEdit && (
              <button onClick={() => { setSettingsOpen(false); setCoachOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-coach-btn">
                <Compass size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> The Coach
              </button>
            )}
            {canEdit && conflictCount > 0 && (
              <button onClick={() => { setSettingsOpen(false); setRecoveredOpen(true); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid="mobile-recovered-btn">
                <History size={14} strokeWidth={1.5} className="text-[var(--brand)]" /> Recovered edits ({conflictCount})
              </button>
            )}
            <button onClick={() => { setSettingsOpen(false); editorRef.current?.openSearch(); }} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="mobile-find-btn">Find &amp; replace</button>
            <button onClick={() => doExport("pdf")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="mobile-export-pdf">Export as PDF</button>
            <button onClick={() => doExport("fdx")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="mobile-export-fdx">Export .fdx</button>
            <button onClick={() => doExport("fountain")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="mobile-export-fountain">Export .fountain</button>
            <button onClick={() => save({ force: true, notify: true })} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="mobile-save-btn">Save now</button>
          </div>
        </div>
      )}

      {/* SLIM CONTROL STRIP — Script ⇄ Board tabs + editor controls */}
      {(!isMobile || linkedBoards.length > 0 || canCreateBoard || canEdit) && (
        <div className="flex items-center flex-wrap gap-1 px-3 min-h-[36px] border-b border-[var(--line)] bg-[var(--paper)] shrink-0 relative z-40" data-testid="editor-board-tabs">
          {editingTab === "script" ? (
            <TabRenameInput value={editValue} onChange={setEditValue} onCommit={commitRename} onCancel={cancelRename} />
          ) : (
            <button
              onClick={() => setActiveTab("script")}
              onDoubleClick={() => canEdit && startRename("script", title)}
              className={boardTabCls(activeTab === "script")}
              data-testid="editor-tab-script"
              title={canEdit ? `${title || "Untitled"} — double-click to rename` : title}
            >
              <FileText size={13} strokeWidth={1.5} /> <span className="truncate max-w-[220px]">{isMobile ? "Script" : `Script — ${title || "Untitled"}`}</span>
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setStoryboardOpened(true); setActiveTab("storyboard"); }}
              className={boardTabCls(activeTab === "storyboard")}
              data-testid="editor-tab-storyboard"
              title="Storyboard — shot lists and panels"
            >
              <Clapperboard size={13} strokeWidth={1.5} /> Storyboard
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setCardsOpened(true); setActiveTab("cards"); }}
              className={boardTabCls(activeTab === "cards")}
              data-testid="editor-tab-cards"
              title="Scene Cards — reorder scenes on index cards"
            >
              <LayoutGrid size={13} strokeWidth={1.5} /> Cards
            </button>
          )}
          {linkedBoards.length > 0 ? (
            editingTab && editingTab !== "script" ? (
              <TabRenameInput value={editValue} onChange={setEditValue} onCommit={commitRename} onCancel={cancelRename} />
            ) : (
              <div className="relative">
                <button
                  onClick={() => setBoardsMenuOpen((o) => !o)}
                  onDoubleClick={() => {
                    if (!canEdit || activeTab === "script") return;
                    const b = linkedBoards.find((x) => x.id === activeTab);
                    if (b) { setBoardsMenuOpen(false); startRename(b.id, b.title); }
                  }}
                  className={boardTabCls(linkedBoards.some((x) => x.id === activeTab) || boardsMenuOpen)}
                  data-testid="editor-boards-btn"
                  title={linkedBoards.some((x) => x.id === activeTab) && canEdit ? "Your boards — double-click to rename this board" : "Your boards"}
                >
                  <Shapes size={13} strokeWidth={1.5} />
                  <span className={`truncate ${isMobile ? "max-w-[110px]" : "max-w-[220px]"}`}>
                    {linkedBoards.some((x) => x.id === activeTab)
                      ? (isMobile ? (linkedBoards.find((x) => x.id === activeTab)?.title || "Board") : `Board — ${linkedBoards.find((x) => x.id === activeTab)?.title || ""}`)
                      : "Boards"}
                  </span>
                  <ChevronDown size={11} strokeWidth={1.5} />
                </button>
                {boardsMenuOpen && (
                  <div className="absolute left-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg py-1 min-w-[230px] z-30" data-testid="boards-menu">
                    {linkedBoards.map((b) => (
                      <div key={b.id} className="flex items-center group/bm hover:bg-[var(--warm-accent)]">
                        <button
                          onClick={() => { setBoardsMenuOpen(false); openBoardTab(b.id); }}
                          className={`flex-1 text-left px-4 py-2 text-sm flex items-center gap-2 min-w-0 ${activeTab === b.id ? "text-[var(--brand)]" : "text-[var(--ink-primary)]"}`}
                          data-testid={`boards-menu-item-${b.id}`}
                        >
                          <Shapes size={13} strokeWidth={1.5} className="shrink-0" /> <span className="truncate">{b.title}</span>
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => deleteBoard(b.id)}
                            className="p-1.5 mr-2 rounded text-[var(--ink-muted)] hover:text-[var(--err)] opacity-0 group-hover/bm:opacity-100 shrink-0"
                            title={`Delete "${b.title}"`}
                            data-testid={`boards-menu-delete-${b.id}`}
                          ><Trash2 size={13} strokeWidth={1.5} /></button>
                        )}
                      </div>
                    ))}
                    {canCreateBoard && (
                      <>
                        <div className="h-px bg-[var(--line)] my-1" />
                        <button
                          onClick={() => { setBoardsMenuOpen(false); createLinkedBoard(); }}
                          disabled={creatingBoard}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--warm-accent)] flex items-center gap-2 text-[var(--ink-secondary)]"
                          data-testid="editor-new-board-btn"
                        >
                          <Plus size={13} strokeWidth={1.5} /> {creatingBoard ? "Creating…" : "New board"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            canCreateBoard && (
              <button onClick={createLinkedBoard} disabled={creatingBoard} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs text-[var(--ink-muted)] hover:text-[var(--brand)] hover:bg-[var(--warm-accent)] transition-colors shrink-0" data-testid="editor-new-board-btn" title="Create a board linked to this script">
                <Plus size={13} strokeWidth={1.5} /> {creatingBoard ? "Creating…" : "New board"}
              </button>
            )
          )}

          {activeTab === "script" ? (
            !isMobile && (
              <div className="flex items-center gap-0.5 shrink-0 pl-2 ml-1 border-l border-[var(--line)]">
                <span className="text-[11px] text-[var(--ink-muted)] px-2 hidden md:inline" data-testid="editor-saved-indicator">
                  {saving ? "Saving…" : savedAt ? `Saved ${timeAgo(savedAt)}` : "—"}
                </span>
                {showPageBreaks && pageInfo.total > 0 && (
                  <span className="text-[11px] text-[var(--ink-muted)] font-mono-script pr-2 hidden lg:inline" data-testid="toolbar-page-info">
                    Page {pageInfo.current} of {pageInfo.total}
                  </span>
                )}
                {isOwner && (
                  <button onClick={() => setShareOpen(true)} className={slimBtnCls()} data-testid="editor-share-btn" title="Share this script">
                    <Share2 size={13} strokeWidth={1.5} /> Share
                  </button>
                )}
                {canEdit && (
                  <>
                    <button onClick={() => editorRef.current?.undo()} className={slimBtnCls()} data-testid="editor-undo-btn" title="Undo (⌘Z)">
                      <Undo2 size={13} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => editorRef.current?.redo()} className={slimBtnCls()} data-testid="editor-redo-btn" title="Redo (⌘⇧Z)">
                      <Redo2 size={13} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => editorRef.current?.openSearch()} className={slimBtnCls()} data-testid="editor-find-btn" title="Find & replace (⌘F)">
                      <Search size={13} strokeWidth={1.5} /> Find
                    </button>
                    <button onClick={() => save({ force: true, notify: true })} className={slimBtnCls()} data-testid="editor-save-btn">
                      <Save size={13} strokeWidth={1.5} /> Save
                    </button>
                  </>
                )}
                <div className="relative">
                  <button onClick={() => setToolsOpen((o) => !o)} className={slimBtnCls(toolsOpen || anyToolOpen)} data-testid="editor-tools-btn" title="Writing tools">
                    <Wrench size={13} strokeWidth={1.5} /> Tools
                  </button>
                  {toolsOpen && (
                    <div className="absolute left-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg py-1 min-w-[210px] z-30" data-testid="tools-menu">
                      <button onClick={() => { setToolsOpen(false); setTimelineOpen((o) => !o); }} className={`w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2 ${timelineOpen ? "text-[var(--brand)]" : ""}`} data-testid="editor-timeline-btn" title="Arrange scenes on a visual timeline">
                        <Rows3 size={14} strokeWidth={1.5} /> Timeline
                      </button>
                      {canEdit && (
                        <button onClick={() => { setToolsOpen(false); setTableReadOpen((o) => !o); }} className={`w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2 ${tableReadOpen ? "text-[var(--brand)]" : ""}`} data-testid="editor-table-read-btn" title="Hear your script read aloud">
                          <Mic size={14} strokeWidth={1.5} /> Table Read
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {canEdit && conflictCount > 0 && (
                  <button onClick={() => setRecoveredOpen((o) => !o)} className={slimBtnCls(recoveredOpen)} data-testid="editor-recovered-btn" title="Recovered edits — overwritten lines you can restore">
                    <History size={13} strokeWidth={1.5} /> Recovered
                    <span className="text-[10px] bg-[var(--brand)] text-white rounded-full px-1.5 py-0.5 leading-none" data-testid="recovered-badge">{conflictCount}</span>
                  </button>
                )}
                <div className="relative">
                  <button onClick={() => setExportOpen((o) => !o)} className={slimBtnCls()} data-testid="editor-export-btn">
                    <FileDown size={13} strokeWidth={1.5} /> Export
                  </button>
                  {exportOpen && (
                    <div className="absolute left-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg py-1 min-w-[180px] z-30" data-testid="export-menu">
                      <button onClick={() => doExport("pdf")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="export-pdf-btn">PDF — for sharing</button>
                      <button onClick={() => doExport("fdx")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="export-fdx-btn">Final Draft (.fdx)</button>
                      <button onClick={() => doExport("fountain")} className="w-full text-left px-4 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="export-fountain-btn">Fountain (.fountain)</button>
                    </div>
                  )}
                </div>
                <SettingsMenu
                  open={settingsOpen}
                  setOpen={setSettingsOpen}
                  showPrompts={showPrompts}
                  togglePrompts={togglePrompts}
                  showPageBreaks={showPageBreaks}
                  togglePageBreaks={togglePageBreaks}
                  mobileFormat={mobileFormat}
                  toggleMobileFormat={toggleMobileFormat}
                  showSceneNumbers={showSceneNumbers}
                  toggleSceneNumbers={toggleSceneNumbers}
                />
              </div>
            )
          ) : activeTab === "storyboard" || activeTab === "cards" ? null : (
            <div className={`flex items-center gap-0.5 shrink-0 ${isMobile ? "basis-full h-8 overflow-x-auto" : "pl-2 ml-1 border-l border-[var(--line)]"}`}>
              <span className="text-[11px] text-[var(--ink-muted)] px-2" data-testid="board-tab-status">
                {boardStatus[activeTab]?.saving ? "Saving…" : boardStatus[activeTab]?.savedAt ? "Saved ✓" : ""}
              </span>
              {canEdit && (
                <>
                  <button
                    onClick={() => {
                      const b = linkedBoards.find((x) => x.id === activeTab);
                      if (b) { setBoardsMenuOpen(false); startRename(b.id, b.title); }
                    }}
                    className={slimBtnCls()}
                    data-testid="board-rename-btn"
                    title="Rename this board"
                  >
                    <Pencil size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Rename</span>
                  </button>
                  <button onClick={() => boardFileInputRef.current?.click()} className={slimBtnCls()} data-testid="board-open-btn" title="Open a .excalidraw file">
                    <FolderOpen size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Open</span>
                  </button>
                  <button onClick={boardSaveToFile} className={slimBtnCls()} data-testid="board-save-file-btn" title="Save the board to a .excalidraw file">
                    <Save size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Save to…</span>
                  </button>
                  <button onClick={boardExportImage} className={slimBtnCls()} data-testid="board-export-image-btn" title="Export the board as a PNG image">
                    <ImageIcon size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Export image</span>
                  </button>
                  <div className="relative">
                    <button onClick={() => setBgOpen((o) => !o)} className={slimBtnCls(bgOpen)} data-testid="board-bg-btn" title="Canvas background">
                      <Palette size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Background</span>
                    </button>
                    {bgOpen && (
                      <div className="absolute left-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg p-2 flex items-center gap-1.5 z-30" data-testid="board-bg-menu">
                        {BG_COLORS.map((c) => (
                          <button key={c} onClick={() => setBoardBackground(c)} className="w-6 h-6 rounded border border-[var(--line)] hover:scale-110 transition-transform" style={{ background: c }} data-testid={`board-bg-${c.slice(1)}`} title={c} />
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={boardClearCanvas} className={slimBtnCls()} data-testid="board-clear-btn" title="Clear the canvas">
                    <Trash2 size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">Clear</span>
                  </button>
                  <input ref={boardFileInputRef} type="file" accept=".excalidraw,application/json" className="hidden" onChange={handleBoardFileChosen} data-testid="board-open-file-input" />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* BODY */}
      <div className="flex flex-1 min-h-0 relative">
        {!isMobile && (
          <ResourcesSidebar
            scriptId={scriptId}
            elements={elements}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            jumpToScene={jumpToScene}
            jumpToElement={jumpToAnchor}
            canGenerate={canEdit}
            charactersRefreshKey={charactersRefreshKey}
            pageInfo={showPageBreaks ? pageInfo : null}
            onLocateCharacter={locateCharacter}
            resizable
            initialWidth={user?.sidebar_width || 300}
            onWidthCommit={persistSidebarWidth}
          />
        )}

        {/* MOBILE: drawer sidebar */}
        {isMobile && mobileSidebarOpen && (
          <div className="fixed inset-0 z-40 flex" data-testid="mobile-sidebar-overlay">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="relative w-[90%] max-w-[360px] bg-[var(--sidebar-bg)] border-r border-[var(--line)] flex flex-col h-full ml-auto">
              <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)] shrink-0">
                <span className="font-display text-base">Resources</span>
                <button onClick={() => setMobileSidebarOpen(false)} className="p-1.5 rounded hover:bg-[var(--warm-accent)]" data-testid="mobile-sidebar-close">
                  <X size={16} strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <ResourcesSidebar
                  scriptId={scriptId}
                  elements={elements}
                  collapsed={false}
                  onToggle={() => setMobileSidebarOpen(false)}
                  jumpToScene={jumpToScene}
                  jumpToElement={jumpToAnchor}
                  canGenerate={canEdit}
                  charactersRefreshKey={charactersRefreshKey}
                  pageInfo={showPageBreaks ? pageInfo : null}
                  onLocateCharacter={locateCharacter}
                  hideChrome
                />
              </div>
            </aside>
          </div>
        )}

        <main className={`flex-1 overflow-y-auto ${isMobile ? "py-4 px-2" : "py-10 px-6"}`} data-testid="editor-canvas-wrap">
          {!isMobile && notepadFull && (
            <NotepadFullView
              scriptId={scriptId}
              noteId={notepadFull}
              canModerate={canModerate}
              currentUserId={user?.id}
              onExit={() => setNotepadFull(null)}
            />
          )}
          <div className={!isMobile && notepadFull ? "hidden" : ""}>
          <ScreenplayEditor
            ref={editorRef}
            initialElements={elements.length ? elements : newBlankScript()}
            showPrompts={showPrompts}
            showPageBreaks={showPageBreaks}
            showSceneNumbers={showSceneNumbers}
            annotations={annotationsOn ? annotations : []}
            onRereadAnnotation={canEdit && annReportId ? rereadAnnotation : null}
            mobileFormat={isMobile && mobileFormat}
            hideBadge={isMobile}
            readOnly={!canEdit}
            onChange={handleEditorChange}
            onSave={() => save({ force: true, notify: true })}
            onPageInfo={handlePageInfo}
          />
          {!isMobile && (
            <div className="text-center text-xs text-[var(--ink-muted)] mt-8 max-w-3xl mx-auto" data-testid="editor-tip">
              <kbd className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] bg-white">Enter</kbd> split & format · <kbd className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] bg-white">Tab</kbd> cycle type · <kbd className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] bg-white">⌘1-7</kbd> force type · <kbd className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] bg-white">⌘Z</kbd> undo · <kbd className="px-1.5 py-0.5 border border-[var(--line)] rounded text-[10px] bg-white">⌘⇧Z</kbd> redo
            </div>
          )}
          </div>
        </main>

        {isMobile && feedbackOpen && (
          <div className="fixed inset-0 z-40 flex" data-testid="mobile-feedback-overlay">
            <div className="absolute inset-0 bg-black/40" onClick={() => setFeedbackOpen(false)} />
            <div className="relative ml-auto h-full">
              <NotepadPanel
                scriptId={scriptId}
                canCreate={canComment}
                canModerate={canModerate}
                currentUserId={user?.id}
                onClose={() => setFeedbackOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Mobile: Supervisor / Coach open as full-screen overlays */}
        {isMobile && (
          <SupervisorPanel
            scriptId={scriptId}
            open={supervisorOpen}
            canEdit={canEdit}
            sceneHeadings={sceneHeadings}
            onClose={() => setSupervisorOpen(false)}
            onJumpToScene={jumpToSceneCard}
          />
        )}

        {isMobile && (
          <CoachPanel
            scriptId={scriptId}
            open={coachOpen}
            onClose={() => setCoachOpen(false)}
            onBeforeSend={flushDraftSave}
            prefill={coachPrefill}
          />
        )}

        {/* Desktop: right Tools sidebar (Feedback / Supervisor / Coverage / Coach) */}
        {!isMobile && toolTabs.length > 0 && (
          <ToolsSidebar
            tabs={toolTabs}
            activeTab={rightTab}
            onActiveTab={setRightTab}
            collapsed={rightCollapsed}
            onToggle={toggleRightCollapsed}
            renderPanel={renderToolPanel}
            initialWidth={user?.right_sidebar_width || 420}
            onWidthCommit={persistRightWidth}
          />
        )}
        {/* Embedded boards — stay mounted after first open so Script ⇄ Board swaps are instant */}
        {linkedBoards.filter((b) => openedBoardTabs.has(b.id)).map((b) => (
          <div key={b.id} className={`board-embed absolute inset-0 z-30 bg-[var(--canvas)] ${activeTab === b.id ? "" : "hidden"}`} data-testid={`board-tab-panel-${b.id}`}>
            <BoardTabPanel
              boardId={b.id}
              onApi={(a) => { boardApisRef.current[b.id] = a; }}
              onStatus={(s) => setBoardStatus((prev) => ({ ...prev, [b.id]: { ...prev[b.id], ...s } }))}
            />
          </div>
        ))}

        {/* Storyboard tab — stays mounted after first open */}
        {storyboardOpened && (
          <div className={`absolute inset-0 z-30 bg-[var(--canvas)] ${activeTab === "storyboard" ? "" : "hidden"}`} data-testid="storyboard-tab-panel">
            <StoryboardView scriptId={scriptId} canEdit={canEdit} scriptTitle={title} />
          </div>
        )}

        {/* Scene Cards tab — stays mounted after first open */}
        {cardsOpened && (
          <div className={`absolute inset-0 z-30 bg-[var(--canvas)] ${activeTab === "cards" ? "" : "hidden"}`} data-testid="cards-tab-panel">
            <SceneCardsBoard
              scriptId={scriptId}
              elements={elements}
              readOnly={!canEdit || isMobile}
              active={activeTab === "cards"}
              onBeforeCommit={flushDraftSave}
              onCommitted={handleCardsCommitted}
            />
          </div>
        )}
      </div>

      {shareOpen && (
        <ShareModal scriptId={scriptId} scriptTitle={title} onClose={() => setShareOpen(false)} />
      )}

      <TableReadPanel
        scriptId={scriptId}
        elements={elements}
        canGenerate={canEdit}
        open={tableReadOpen}
        onClose={() => setTableReadOpen(false)}
      />

      {isMobile && (
        <CoveragePanel
          scriptId={scriptId}
          open={coverageOpen}
          onClose={() => setCoverageOpen(false)}
          annotationsOn={annotationsOn}
          onToggleAnnotations={toggleAnnotations}
          onAnnotations={applyAnnotations}
          scriptGenre={script?.genre || ""}
          onBeforeRun={flushDraftSave}
          editSignal={savedAt}
          onCoachFocus={handleCoachFocus}
        />
      )}

      {activeTab === "script" && charHighlight && (
        <>
          <style>{`
            ${charHighlight.ids.map((eid) => `[data-element-id="${eid}"]`).join(",")} { background: rgba(211,110,90,0.08); }
            ${charHighlight.strongIds.map((eid) => `[data-element-id="${eid}"]`).join(",")} { background: rgba(211,110,90,0.22); border-radius: 2px; }
          `}</style>
          <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full bg-[var(--ink-primary)] text-white shadow-xl text-xs font-sans" data-testid="char-highlight-chip">
            <MapPin size={12} className="opacity-80 shrink-0" />
            <span className="whitespace-nowrap">
              <strong className="uppercase tracking-wide">{charHighlight.name}</strong> — in {charHighlight.scenes} scene{charHighlight.scenes === 1 ? "" : "s"}
            </span>
            <button onClick={() => setCharHighlight(null)} className="p-1 rounded-full hover:bg-white/20" data-testid="char-highlight-clear" title="Clear the highlights">
              <X size={12} />
            </button>
          </div>
        </>
      )}

      <RecoveredEditsPanel
        scriptId={scriptId}
        open={recoveredOpen}
        onClose={() => setRecoveredOpen(false)}
        onInsertLine={insertRecoveredLine}
        onChanged={refreshConflicts}
      />

      <TimelinePanel
        scriptId={scriptId}
        elements={elements}
        open={timelineOpen}
        fullscreen={timelineFull}
        canEdit={canEdit}
        onClose={() => { setTimelineOpen(false); setTimelineFull(false); }}
        onToggleFullscreen={() => setTimelineFull((f) => !f)}
        onExitFullscreen={() => setTimelineFull(false)}
        onReorderScript={reorderScript}
        onJumpToScene={jumpToSceneCard}
        onReadLanes={(ids) => setLaneRead({ laneIds: ids })}
        initialCardW={user?.timeline_card_w || 200}
        initialCardH={user?.timeline_card_h || 170}
        onPersistCardSize={persistCardSize}
      />
      {laneRead && (
        <LaneReadView
          scriptId={scriptId}
          elements={elements}
          initialLaneIds={laneRead.laneIds}
          onClose={() => setLaneRead(null)}
          onJumpToScene={(sid) => {
            setLaneRead(null);
            setTimelineFull(false);
            setTimeout(() => jumpToAnchor(sid), 150);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile top bar
// ---------------------------------------------------------------------------
function MobileTopBar({ title, pageInfo, onTitleChange, onTitleBlur, onOpenSidebar, onUndo, onRedo, onOpenSettings, canEdit = true, showFeedback = false, onOpenFeedback }) {
  return (
    <header className="bg-[var(--canvas)]/95 backdrop-blur border-b border-[var(--line)] z-20" data-testid="mobile-header">
      <div className="flex items-center px-2 h-12 gap-1">
        <Link to="/app" className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-home-btn" title="My scripts">
          <Home size={18} strokeWidth={1.5} />
        </Link>
        <input
          value={title}
          onChange={onTitleChange}
          onBlur={onTitleBlur}
          placeholder="Untitled"
          readOnly={!canEdit}
          data-testid="mobile-title-input"
          className="flex-1 min-w-0 font-display text-base bg-transparent outline-none border-b border-transparent focus:border-[var(--brand)] py-1"
        />
        {canEdit && <button onClick={onUndo} className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-undo-btn" title="Undo"><Undo2 size={16} strokeWidth={1.5} /></button>}
        {canEdit && <button onClick={onRedo} className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-redo-btn" title="Redo"><Redo2 size={16} strokeWidth={1.5} /></button>}
        {showFeedback && <button onClick={onOpenFeedback} className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-feedback-btn" title="Notepad"><NotebookPen size={16} strokeWidth={1.5} /></button>}
        <button onClick={onOpenSidebar} className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-resources-btn" title="Resources">
          <BookText size={18} strokeWidth={1.5} />
        </button>
        <button onClick={onOpenSettings} className="p-2 text-[var(--ink-secondary)]" data-testid="mobile-settings-btn" title="More">
          <Settings2 size={16} strokeWidth={1.5} />
        </button>
      </div>
      {pageInfo?.total > 1 && (
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] text-center pb-1" data-testid="mobile-page-info">
          Page {pageInfo.current} of {pageInfo.total}
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Settings menu (desktop dropdown)
// ---------------------------------------------------------------------------
function SettingsMenu({ open, setOpen, ...rest }) {
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className={slimBtnCls(open)} data-testid="editor-settings-btn" title="Editor settings">
        <Settings2 size={13} strokeWidth={1.5} /> Settings
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg py-2 min-w-[280px] z-30" data-testid="settings-menu">
          <SettingsItems {...rest} />
        </div>
      )}
    </div>
  );
}

function SettingsItems({ showPrompts, togglePrompts, showPageBreaks, togglePageBreaks, mobileFormat, toggleMobileFormat, showSceneNumbers, toggleSceneNumbers }) {
  return (
    <>
      <SettingsToggle
        label="Show prompts"
        hint='Placeholder hints like "Action / Dialogue"'
        checked={showPrompts}
        onChange={togglePrompts}
        testId="settings-prompts-toggle"
      />
      <SettingsToggle
        label="Show page breaks"
        hint="Industry page boundaries + page counter"
        checked={showPageBreaks}
        onChange={togglePageBreaks}
        testId="settings-pagebreaks-toggle"
      />
      <SettingsToggle
        label="Scene numbers"
        hint="Production format — numbers in both margins"
        checked={showSceneNumbers}
        onChange={toggleSceneNumbers}
        testId="settings-scenenumbers-toggle"
      />
      <SettingsToggle
        label="Format for mobile"
        hint="Strip indents while writing on phone"
        checked={mobileFormat}
        onChange={toggleMobileFormat}
        testId="settings-mobileformat-toggle"
      />
    </>
  );
}

function SettingsToggle({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 px-4 py-2 cursor-pointer hover:bg-[var(--warm-accent)]">
      <div>
        <div className="text-sm text-[var(--ink-primary)]">{label}</div>
        <div className="text-[11px] text-[var(--ink-muted)] mt-0.5">{hint}</div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testId}
        className="accent-[var(--brand)] w-4 h-4 cursor-pointer"
      />
    </label>
  );
}

function timeAgo(d) {
  const ms = Date.now() - d.getTime();
  if (ms < 5000) return "just now";
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  return `${Math.floor(ms / 60000)}m ago`;
}

const boardTabCls = (active) =>
  `inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-xs font-medium transition-colors shrink-0 ${
    active
      ? "bg-[var(--brand-soft)] text-[var(--brand)]"
      : "text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] hover:text-[var(--ink-primary)]"
  }`;

const slimBtnCls = (active = false) =>
  `inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium transition-colors shrink-0 ${
    active
      ? "text-[var(--brand)] bg-[var(--brand-soft)]"
      : "text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] hover:text-[var(--ink-primary)]"
  }`;

const BG_COLORS = ["#ffffff", "#f8f9fa", "#f5faff", "#fffce8", "#fdf8f6"];

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function TabRenameInput({ value, onChange, onCommit, onCancel }) {
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") onCancel();
      }}
      className="h-7 px-2 text-xs rounded-md border border-[var(--brand)] bg-white outline-none w-52 shrink-0"
      data-testid="tab-rename-input"
    />
  );
}

